import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useCRMStore } from '@/stores/useCRMStore';
import { useOrgStore } from '@/stores/useOrgStore';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

// Check if we're in dev mode (same check as sessionGuard)
const DEV_MODE = true;

const stages = [
  { key: 'new', label: 'New', color: 'bg-blue-500' },
  { key: 'rpc', label: 'RPC', color: 'bg-purple-500' },
  { key: 'aod', label: 'AOD', color: 'bg-orange-500' },
  { key: 'pending_arrangement', label: 'Arrangement', color: 'bg-yellow-500' },
  { key: 'qc', label: 'QC', color: 'bg-green-500' },
  { key: 'active', label: 'Active', color: 'bg-emerald-500' },
  { key: 'soft', label: 'Soft/Admin', color: 'bg-red-500' },
  { key: 'customer_care', label: 'Customer Care', color: 'bg-pink-500' },
];

export default function KanbanBoard() {
  const { activeOrg } = useOrgStore();
  const { cases, setCases, setSelectedCase } = useCRMStore();
  const [loading, setLoading] = useState(!DEV_MODE);
  const [draggedCase, setDraggedCase] = useState<string | null>(null);

  useEffect(() => {
    if (!activeOrg || DEV_MODE) {
      setLoading(false);
      setCases([]);
      return;
    }
    loadCases();
  }, [activeOrg]);

  const loadCases = async () => {
    try {
      setLoading(true);
      // Using any to bypass type issues with schema notation
      const response: any = await supabase
        .from('crm.debtor_cases' as any)
        .select('*')
        .eq('org_id', activeOrg);

      if (response.error) {
        console.error('Error loading cases:', response.error);
        setCases([]);
        return;
      }

      setCases(response.data || []);
    } catch (error) {
      console.error('Error loading cases:', error);
      setCases([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (caseId: string) => {
    setDraggedCase(caseId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (targetStage: string) => {
    if (!draggedCase) return;

    try {
      const { error } = await supabase.functions.invoke('crm-transition', {
        body: {
          case_id: draggedCase,
          target_stage: targetStage,
        },
      });

      if (error) throw error;

      toast.success('Case transitioned successfully');
      loadCases();
    } catch (error) {
      console.error('Error transitioning case:', error);
      toast.error('Failed to transition case');
    } finally {
      setDraggedCase(null);
    }
  };

  const getCasesByStage = (stage: string) => {
    return cases.filter((c) => c.stage === stage);
  };

  if (loading) {
    return <div className="text-center py-8">Loading cases...</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {stages.map((stage) => {
        const stageCases = getCasesByStage(stage.key);
        
        return (
          <div
            key={stage.key}
            className="flex flex-col"
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(stage.key)}
          >
            <div className="mb-3 flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${stage.color}`} />
              <h3 className="font-semibold">{stage.label}</h3>
              <Badge variant="secondary">{stageCases.length}</Badge>
            </div>

            <div className="space-y-2 min-h-[200px] flex-1 border border-border rounded-lg p-3 bg-card/50">
              {stageCases.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No cases
                </div>
              ) : (
                stageCases.map((caseData) => {
                  return (
                    <motion.div
                      key={caseData.id}
                      draggable
                      onDragStart={() => handleDragStart(caseData.id)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedCase(caseData)}
                    >
                      <Card className="p-3 cursor-pointer hover:shadow-lg transition-shadow">
                        <div className="space-y-2">
                          <div className="flex justify-between items-start">
                            <span className="text-sm font-medium">
                              Case: ...{String(caseData.account_id).slice(-8)}
                            </span>
                            {caseData.neg_outcomes_count > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                {caseData.neg_outcomes_count} neg
                              </Badge>
                            )}
                          </div>
                          
                          {caseData.next_action_at && (
                            <div className="text-xs text-muted-foreground">
                              Next: {new Date(caseData.next_action_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </Card>
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
